import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AbstractWebSocketService, WebSocketServiceConfig, WebSocketRequestOptions } from 'types';

import { HWServiceConfig } from './hw-service-config';
import { BatteryInfo, BatteryState, CsumStatus, IntegrityInfo } from './hw-service-model';

@Injectable()
export abstract class SystemServiceFactory {
  abstract create(config: HWServiceConfig): SystemService;
}

export class SystemService extends AbstractWebSocketService {
  public constructor(config: HWServiceConfig) {
    super(new WebSocketServiceConfig(config.getHWServiceUrl(), config.getSystemServiceProtocol()));
  }

  getBatteryReport(): Observable<BatteryInfo> {
    let request = { command: 'get-battery-capacity', progress: false };
    return this.sendRequest(request, new WebSocketRequestOptions()).pipe(
      map((r) => {
        return this.mapBatteryReport(r);
      }),
    );
  }

  getCpuSerial(): Observable<string> {
    const request = { command: 'get-cpu-serial' };
    return this.sendRequest(request, new WebSocketRequestOptions()).pipe(map((r: any) => r['cpu-serial']));
  }

  getUseCheckFingerprints(): Observable<boolean | undefined> {
    const request = { command: 'get-use-check-fingerprints' };
    return this.sendRequest(request, new WebSocketRequestOptions()).pipe(
      map((r) => {
        return this.mapUseCheckFingerprints(r);
      }),
    );
  }

  checkIntegrity(): Observable<number | IntegrityInfo> {
    let request = { command: 'check-integrity', progress: true };
    return this.sendRequest(request, new WebSocketRequestOptions()).pipe(
      map((r) => {
        return this.mapIntegrityReport(r);
      }),
    );
  }

  isTimeCorrect(): Observable<boolean> {
    let request = { command: 'is-date-correct', progress: false };
    return this.sendRequest(request, new WebSocketRequestOptions()).pipe(
      map((_) => true),
      catchError((_) => of(false)),
    );
  }

  setTime(date: string): Observable<void> {
    let request = { command: 'set-date', date: date, progress: false };
    return this.sendRequest(request, new WebSocketRequestOptions()).pipe(map((r) => undefined));
  }

  unmountFlashDrive(): Observable<void> {
    return of(undefined);
  }

  openPowerMenu(): Observable<void> {
    let request = { command: 'open-power-menu', progress: false };
    return this.sendRequest(request, new WebSocketRequestOptions()).pipe(map((r) => undefined));
  }

  log(text: string): Observable<void> {
    let request = { command: 'log', text: text, progress: false };
    return this.sendRequest(request, new WebSocketRequestOptions()).pipe(map((r) => undefined));
  }

  shutdown(): Observable<void> {
    let request = { command: 'shutdown', progress: false };
    return this.sendRequest(request, new WebSocketRequestOptions()).pipe(map((r) => undefined));
  }

  reboot(): Observable<void> {
    let request = { command: 'reboot', progress: false };
    return this.sendRequest(request, new WebSocketRequestOptions()).pipe(map((r) => undefined));
  }

  suspend(): Observable<void> {
    let request = { command: 'suspend', progress: false };
    return this.sendRequest(request, new WebSocketRequestOptions()).pipe(map((r) => undefined));
  }

  standby(): Observable<void> {
    let request = { command: 'set-op-mode', mode: 'standby', progress: false };
    return this.sendRequest(request, new WebSocketRequestOptions()).pipe(map((r) => undefined));
  }

  private mapBatteryReport(response: any): BatteryInfo {
    if (response instanceof Blob) {
      throw 'Invalid response type (Blob)';
    }
    let result = new BatteryInfo();
    result.capacity = response.capacity;
    switch (response.status) {
      case 'unknown':
        result.state = BatteryState.Unknown;
        break;
      case 'absent':
        result.state = BatteryState.Absent;
        break;
      case 'full':
        result.state = BatteryState.Full;
        break;
      case 'charging':
        result.state = BatteryState.Charging;
        break;
      case 'discharging':
        result.state = BatteryState.Discharging;
        break;
      case 'not charging':
        result.state = BatteryState.NotCharging;
        break;
      default:
        result.state = BatteryState.Unknown;
        break;
    }
    return result;
  }

  private mapUseCheckFingerprints(response: any): boolean | undefined {
    if (response instanceof Blob) {
      throw 'Invalid response type (Blob)';
    }
    let result: boolean | undefined;
    switch (response.mode) {
      case 'yes':
        result = true;
        break;
      case 'no':
        result = false;
        break;
      default:
        result = undefined;
        break;
    }
    return result;
  }

  private mapIntegrityReport(response: any): number | IntegrityInfo {
    if (response instanceof Blob) {
      throw 'Invalid response type (Blob)';
    }
    if (typeof response.progress === 'number') {
      return response.progress;
    }
    const result = new IntegrityInfo();
    result.valid = response.valid;
    result.csums = response.csums;
    result.csums_status = response.csums_status;
    for (let i = 0; i <= result.csums.length - 1; i++) {
      if (typeof response.csums_status[i] === 'boolean') {
        response.csums_status[i] ? (result.csums_status[i] = CsumStatus.Valid) : (result.csums_status[i] = CsumStatus.Invalid);
      } else {
        result.csums_status[i] = CsumStatus.NotChecked;
      }
    }
    result.messages = response.messages.map((message: string) => {
      message = message.replace('INIT_NUMBER_NOT_FOUND', 'Не найден инициализационный файл учётного номера устройства.');
      message = message.replace('INIT_NUMBER_INCORRECT', 'Неверный формат инициализационного файла учётного номера устройства.');
      message = message.replace('INIT_NUMBER=', 'Учётный номер устройства: ');
      message = message.replace('INIT_DATE_NOT_FOUND', 'Не найден инициализационный файл даты инициализации устройства.');
      message = message.replace('INIT_DATE_INCORRECT', 'Неверный формат инициализационного файла даты инициализации устройства');
      message = message.replace('INIT_DATE_TOO_SMALL', 'Дата инициализации устройства некорректна.');
      message = message.replace('SYSTEM_DATE_TOO_SMALL', 'Системное время устройства некорректно.');
      message = message.replace('INIT_DATE=', 'Дата инициализации устройства: ');
      message = message.replace('INIT_CPU_SERIAL_NOT_FOUND', 'Не найден инициализационный файл серийного номера процессора.');
      message = message.replace('INIT_CPU_SERIAL_INCORRECT', 'Неверный формат инициализационного файла серийного номера процессора.');
      message = message.replace('SYSTEM_CPU_SERIAL_UNKNOWN', 'Не удалось считать серийный номер установленного процессора.');
      message = message.replace('SYSTEM_CPU_SERIAL_MISMATCH', 'Серийный номер установленного процессора некорректен.');
      message = message.replace('INIT_CPU_SERIAL=', 'Серийный номер процессора: ');
      message = message.replace('CONTROL_SUM_NOT_FOUND', 'Не найден файл контрольных сумм.');
      message = message.replace('CONTROL_SUM_MISMATCH', 'Контрольные суммы неверны.');
      message = message.replace('CONTROL_SUM_MATCH', 'Контрольные суммы верны.');
      return message;
    });
    return result;
  }
}
